'use client';

// Galexia Connections — connect external apps (Composio) and authenticate
// browser sessions (Kernel managed auth). Port of apps/web's
// app/connections/page.tsx (an 867-line async server component with an
// inline `'use server'` action) + app/connections/browser-login-actions.ts
// (3 more actions) + app/connections/browser-login-controls.tsx.
//
// Unlike /agents or /memory, apps/web's original had NO separate client
// component to alias-mount — every DB/SDK read happened inline at render
// time, and every write was a server action. This file is a genuine rewrite
// into a client component: the presentational logic (curated connector
// catalog, status derivation, category grouping, the connector/browser-login
// row markup) is ported near-verbatim; every DB/SDK read becomes a fetch to
// `apps/server/src/routes/connections.ts`, and every server action becomes an
// async handler that POSTs/DELETEs to the same routes. See that file's header
// for the old-action → new-endpoint table.
//
// SIMPLIFICATIONS vs the original (thin-mount rule — flagged, not hidden):
//  - Search/category filters are local component state, not URL search
//    params (`?q&category`) — no full-page reload existed to preserve state
//    across anyway; the toolkit catalog is fetched once (unfiltered, limit
//    100) and filtered entirely client-side, so typing in the search box no
//    longer re-hits the server per keystroke.
//  - The old `?login=<id>#browser-logins` redirect-then-refresh dance after
//    starting a login is now a direct state update (no navigation): creating
//    or restarting a login updates that one row in place.
//  - `?error=` / `#browser-logins` anchor banners are now inline error state
//    set directly by the failing handler.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CalendarBlank,
  CheckCircle,
  Clock,
  Database,
  EnvelopeSimple,
  GithubLogo,
  GlobeSimple,
  GoogleDriveLogo,
  GoogleLogo,
  InstagramLogo,
  LinkSimple,
  LockKey,
  MagnifyingGlass,
  Megaphone,
  NotionLogo,
  PlugsConnected,
  Plus,
  PuzzlePiece,
  SignIn,
  SlackLogo,
  Cube,
  Table,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDocumentTitle } from '@/lib/use-document-title';
import { DeleteBrowserLoginButton } from './browser-login-controls';
import type { AuthConnectionSummary, ComposioConnectedAccountSummary, ComposioToolkitSummary, UserMcpConnectorSummary } from './types';

type ConnectionStatus = 'active' | 'pending' | 'expired' | 'disabled' | 'available';

type PhosphorIcon = (props: { className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }) => ReactNode;

type Connector = {
  slug: string;
  name: string;
  description: string;
  category: string;
  icon: PhosphorIcon;
  accent: string;
  prompt: string;
};

const FEATURED_CONNECTORS: Connector[] = [
  {
    slug: 'gmail',
    name: 'Gmail',
    description: 'Draft replies, search your inbox, and summarize email threads.',
    category: 'Productivity',
    icon: EnvelopeSimple,
    accent: 'from-red-500/30 to-amber-500/10 text-red-300',
    prompt: 'Connect my Gmail account so Galexia can help with email.',
  },
  {
    slug: 'github',
    name: 'GitHub',
    description: 'Manage repositories, issues, pull requests, and project code.',
    category: 'Developer',
    icon: GithubLogo,
    accent: 'from-zinc-300/20 to-sky-500/10 text-zinc-100',
    prompt: 'Connect my GitHub account so Galexia can help with repositories and issues.',
  },
  {
    slug: 'googledrive',
    name: 'Google Drive',
    description: 'Find files, inspect source material, and organize documents.',
    category: 'Productivity',
    icon: GoogleDriveLogo,
    accent: 'from-emerald-500/25 to-sky-500/10 text-emerald-300',
    prompt: 'Connect my Google Drive so Galexia can find and use my files.',
  },
];

const CONNECTORS: Connector[] = [
  ...FEATURED_CONNECTORS,
  {
    slug: 'googlecalendar',
    name: 'Google Calendar',
    description: 'Read schedules, plan work blocks, and prepare meeting context.',
    category: 'Productivity',
    icon: CalendarBlank,
    accent: 'from-blue-500/25 to-cyan-500/10 text-blue-300',
    prompt: 'Connect my Google Calendar so Galexia can help with my schedule.',
  },
  {
    slug: 'googledocs',
    name: 'Google Docs',
    description: 'Create, search, summarize, and update workspace documents.',
    category: 'Productivity',
    icon: GoogleLogo,
    accent: 'from-sky-500/25 to-blue-500/10 text-sky-300',
    prompt: 'Connect my Google Docs so Galexia can help with documents.',
  },
  {
    slug: 'googlesheets',
    name: 'Google Sheets',
    description: 'Read tables, create trackers, and update structured data.',
    category: 'Data',
    icon: Table,
    accent: 'from-emerald-500/25 to-lime-500/10 text-emerald-300',
    prompt: 'Connect my Google Sheets so Galexia can work with spreadsheets.',
  },
  {
    slug: 'slack',
    name: 'Slack',
    description: 'Search channels, summarize threads, and draft team updates.',
    category: 'Productivity',
    icon: SlackLogo,
    accent: 'from-fuchsia-500/25 to-amber-500/10 text-fuchsia-300',
    prompt: 'Connect my Slack workspace so Galexia can help with team messages.',
  },
  {
    slug: 'notion',
    name: 'Notion',
    description: 'Search workspace content, update notes, and maintain pages.',
    category: 'Productivity',
    icon: NotionLogo,
    accent: 'from-neutral-300/20 to-violet-500/10 text-neutral-100',
    prompt: 'Connect my Notion workspace so Galexia can help with notes and pages.',
  },
  {
    slug: 'linear',
    name: 'Linear',
    description: 'Create issues, inspect roadmaps, and update engineering tasks.',
    category: 'Developer',
    icon: PuzzlePiece,
    accent: 'from-violet-500/25 to-indigo-500/10 text-violet-300',
    prompt: 'Connect my Linear workspace so Galexia can help with issues and projects.',
  },
  {
    slug: 'instagram',
    name: 'Instagram',
    description: 'Plan posts, inspect campaign context, and prepare creative work.',
    category: 'Social',
    icon: InstagramLogo,
    accent: 'from-pink-500/25 to-orange-500/10 text-pink-300',
    prompt: 'Connect Instagram so Galexia can help with social content workflows.',
  },
  {
    slug: 'metaads',
    name: 'Meta Ads Manager',
    description: 'Review campaign context and draft optimization plans.',
    category: 'Social',
    icon: Megaphone,
    accent: 'from-blue-500/25 to-indigo-500/10 text-blue-300',
    prompt: 'Connect Meta Ads Manager so Galexia can help with ad campaign analysis.',
  },
  {
    slug: 'postgres',
    name: 'Postgres',
    description: 'Query databases, inspect schemas, and create data reports.',
    category: 'Data',
    icon: Database,
    accent: 'from-cyan-500/25 to-blue-500/10 text-cyan-300',
    prompt: 'Connect a Postgres database so Galexia can help with data analysis.',
  },
];

const CATEGORY_ORDER = ['Productivity', 'Developer', 'Data', 'Social'] as const;
const CONNECTORS_BY_SLUG = new Map(CONNECTORS.map((connector) => [connector.slug, connector]));
const DEFAULT_CONNECTOR_ACCENT = 'from-slate-300/15 to-cyan-500/10 text-cyan-200';

function titleFromSlug(slug: string) {
  return slug.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function categoryFromToolkit(toolkit: ComposioToolkitSummary) {
  const categories = toolkit.categories.map((category) => category.toLowerCase());
  if (categories.some((category) => category.includes('developer') || category.includes('code'))) return 'Developer';
  if (categories.some((category) => category.includes('data') || category.includes('database'))) return 'Data';
  if (categories.some((category) => category.includes('social') || category.includes('marketing'))) return 'Social';
  if (toolkit.slug.includes('github') || toolkit.slug.includes('linear') || toolkit.slug.includes('jira')) return 'Developer';
  if (toolkit.slug.includes('sql') || toolkit.slug.includes('postgres') || toolkit.slug.includes('sheet')) return 'Data';
  if (toolkit.slug.includes('instagram') || toolkit.slug.includes('twitter') || toolkit.slug.includes('facebook') || toolkit.slug.includes('ads')) return 'Social';
  return 'Productivity';
}

function connectorFromToolkit(toolkit: ComposioToolkitSummary): Connector {
  const curated = CONNECTORS_BY_SLUG.get(toolkit.slug);
  if (curated) {
    return { ...curated, name: toolkit.name || curated.name, description: toolkit.description || curated.description };
  }
  const name = toolkit.name || titleFromSlug(toolkit.slug);
  return {
    slug: toolkit.slug,
    name,
    description: toolkit.description || `Connect ${name} so Galexia can use it when you authorize access.`,
    category: categoryFromToolkit(toolkit),
    icon: PuzzlePiece,
    accent: DEFAULT_CONNECTOR_ACCENT,
    prompt: `Connect my ${name} account so Galexia can help with it.`,
  };
}

function mergeConnectors(toolkits: ComposioToolkitSummary[], includeCurated: boolean) {
  const bySlug = new Map<string, Connector>();
  if (includeCurated) {
    for (const connector of CONNECTORS) bySlug.set(connector.slug, connector);
  }
  for (const toolkit of toolkits) bySlug.set(toolkit.slug, connectorFromToolkit(toolkit));
  return [...bySlug.values()].sort((a, b) => {
    const aFeatured = FEATURED_CONNECTORS.some((connector) => connector.slug === a.slug);
    const bFeatured = FEATURED_CONNECTORS.some((connector) => connector.slug === b.slug);
    if (aFeatured !== bFeatured) return aFeatured ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function normalizeStatus(account?: ComposioConnectedAccountSummary): ConnectionStatus {
  if (!account) return 'available';
  if (account.isDisabled) return 'disabled';
  const status = account.status.toUpperCase();
  if (status === 'ACTIVE') return 'active';
  if (status === 'EXPIRED' || status === 'FAILED') return 'expired';
  if (status === 'PENDING' || status === 'INITIATED') return 'pending';
  return 'available';
}

function statusLabel(status: ConnectionStatus) {
  if (status === 'active') return 'Connected';
  if (status === 'pending') return 'Pending';
  if (status === 'expired') return 'Needs attention';
  if (status === 'disabled') return 'Disabled';
  return 'Available';
}

function statusClasses(status: ConnectionStatus) {
  if (status === 'active') return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300';
  if (status === 'pending') return 'border-amber-400/30 bg-amber-500/10 text-amber-300';
  if (status === 'expired') return 'border-orange-400/30 bg-orange-500/10 text-orange-300';
  if (status === 'disabled') return 'border-zinc-400/20 bg-zinc-500/10 text-zinc-300';
  return 'border-white/10 bg-white/[0.04] text-muted-foreground';
}

function StatusIcon({ status }: { status: ConnectionStatus }) {
  if (status === 'active') return <CheckCircle weight="fill" className="size-3.5" />;
  if (status === 'pending') return <Clock weight="duotone" className="size-3.5" />;
  if (status === 'expired' || status === 'disabled') return <WarningCircle weight="duotone" className="size-3.5" />;
  return <Plus weight="bold" className="size-3.5" />;
}

function chatHref(prompt: string) {
  return `/?q=${encodeURIComponent(prompt)}`;
}

function findLatestByToolkit(accounts: ComposioConnectedAccountSummary[]) {
  const bySlug = new Map<string, ComposioConnectedAccountSummary>();
  for (const account of accounts) {
    const existing = bySlug.get(account.toolkitSlug);
    if (!existing) {
      bySlug.set(account.toolkitSlug, account);
      continue;
    }
    const existingTime = new Date(existing.updatedAt ?? existing.createdAt ?? 0).getTime();
    const nextTime = new Date(account.updatedAt ?? account.createdAt ?? 0).getTime();
    if (nextTime > existingTime) bySlug.set(account.toolkitSlug, account);
  }
  return bySlug;
}

// Compact 72px connector row: icon chip + name + one-line description +
// circular action. Connected accounts swap the "+" for a status affordance.
function ConnectorCard({
  connector,
  account,
  busy,
  onAuthorize,
}: {
  connector: Connector;
  account?: ComposioConnectedAccountSummary;
  busy: boolean;
  onAuthorize: (connector: Connector) => void;
}) {
  const Icon = connector.icon;
  const status = normalizeStatus(account);
  const actionPrompt = status === 'active' ? `Use my connected ${connector.name} account to help me.` : connector.prompt;

  const action = status === 'active' ? (
    <Link
      href={chatHref(actionPrompt)}
      aria-label={`Use ${connector.name} in chat`}
      className="grid size-8 shrink-0 place-items-center rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-400 transition-colors hover:bg-emerald-500/20"
    >
      <CheckCircle weight="fill" className="size-4" />
    </Link>
  ) : (
    <button
      type="button"
      disabled={busy}
      onClick={() => onAuthorize(connector)}
      aria-label={`Connect ${connector.name}`}
      className={cn(
        'grid size-8 shrink-0 place-items-center rounded-full transition-colors disabled:opacity-50',
        status === 'expired' || status === 'disabled'
          ? 'border border-amber-400/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
          : 'bg-muted text-muted-foreground group-hover:bg-violet-500/15 group-hover:text-violet-400',
      )}
    >
      {status === 'expired' || status === 'disabled'
        ? <WarningCircle weight="duotone" className="size-4" />
        : <Plus weight="bold" className="size-4" />}
    </button>
  );

  return (
    <article className="group flex h-[72px] items-center justify-between gap-2 rounded-xl border border-border/50 bg-card/60 p-3 transition-all hover:border-violet-500/40 hover:bg-card">
      <div className="flex min-w-0 items-center gap-3 pr-1">
        <div className="relative grid size-9 shrink-0 place-items-center overflow-hidden rounded-[10px] border border-border/60 bg-background">
          <div className={cn('pointer-events-none absolute inset-0 bg-gradient-to-br opacity-60', connector.accent)} />
          <Icon weight="duotone" className="relative size-5" />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{connector.name}</span>
            {status === 'pending' && (
              <Badge className={cn('shrink-0 gap-1 rounded-full border px-1.5 py-0 text-[10px]', statusClasses(status))}>
                <StatusIcon status={status} />
                {statusLabel(status)}
              </Badge>
            )}
          </div>
          <p className="truncate text-xs text-muted-foreground transition-colors group-hover:text-muted-foreground/90">
            {connector.description}
          </p>
        </div>
      </div>
      {action}
    </article>
  );
}

// Browser login row: domain + profile name + AUTHENTICATED / NEEDS LOGIN
// badge, with hosted-login link (when a flow is live), re-login, refresh, and
// delete.
function BrowserLoginRow({
  connection,
  busy,
  onStartLogin,
  onRefresh,
  onDelete,
}: {
  connection: AuthConnectionSummary;
  busy: boolean;
  onStartLogin: (connectionId: string) => void;
  onRefresh: (connectionId: string) => void;
  onDelete: (connectionId: string) => Promise<void>;
}) {
  const authed = connection.authenticated;
  return (
    <article className="group flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-card/60 p-3 transition-all hover:border-violet-500/40 hover:bg-card">
      <div className="flex min-w-0 items-center gap-3">
        <div className="relative grid size-9 shrink-0 place-items-center overflow-hidden rounded-[10px] border border-border/60 bg-background">
          <div className={cn('pointer-events-none absolute inset-0 bg-gradient-to-br opacity-60', authed ? 'from-emerald-500/25 to-violet-500/10' : 'from-amber-500/25 to-violet-500/10')} />
          <GlobeSimple weight="duotone" className="relative size-5 text-violet-300" />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-medium">{connection.domain}</span>
          <span className="truncate text-[11px] text-muted-foreground">
            Profile <span className="font-mono text-muted-foreground/90">{connection.profileSlug}</span>
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Badge
          className={cn(
            'gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            authed ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300' : 'border-amber-400/30 bg-amber-500/10 text-amber-300',
          )}
        >
          {authed ? <CheckCircle weight="fill" className="size-3.5" /> : <WarningCircle weight="duotone" className="size-3.5" />}
          {authed ? 'Authenticated' : 'Needs login'}
        </Badge>

        {connection.hostedUrl ? (
          <a
            href={connection.hostedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-8 items-center gap-1.5 rounded-full bg-violet-500 px-3 text-xs font-semibold text-white transition-colors hover:bg-violet-400"
          >
            <ArrowSquareOut weight="bold" className="size-3.5" />
            Open login page
          </a>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => onStartLogin(connection.id)}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-violet-500/40 px-3 text-xs font-semibold text-violet-300 transition-colors hover:bg-violet-500/10 disabled:opacity-50"
          >
            <SignIn weight="bold" className="size-3.5" />
            {authed ? 'Re-login' : 'Start login'}
          </button>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={() => onRefresh(connection.id)}
          aria-label={`Refresh status for ${connection.domain}`}
          className="grid size-8 shrink-0 place-items-center rounded-full border border-border/70 text-muted-foreground transition-colors hover:border-violet-500/40 hover:text-violet-300 disabled:opacity-50"
        >
          <ArrowsClockwise weight="bold" className="size-4" />
        </button>

        <DeleteBrowserLoginButton connectionId={connection.id} label={connection.domain} onDelete={onDelete} />
      </div>
    </article>
  );
}

// Custom MCP connectors: a user-hosted Model Context Protocol server whose
// tools are injected into chat turns next to the Composio catalog. Matches the
// browser-logins section shape: an add form + a list of rows with an enabled
// toggle, a Test button (shows discovered tools), and delete.
function McpConnectorRow({
  connector,
  busy,
  onToggle,
  onTest,
  onDelete,
}: {
  connector: UserMcpConnectorSummary;
  busy: boolean;
  onToggle: (connector: UserMcpConnectorSummary) => void;
  onTest: (id: string) => void;
  onDelete: (id: string, name: string) => void;
}) {
  const health = connector.lastHealth;
  return (
    <article className="group flex flex-col gap-2 rounded-xl border border-border/50 bg-card/60 p-3 transition-all hover:border-violet-500/40 hover:bg-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative grid size-9 shrink-0 place-items-center overflow-hidden rounded-[10px] border border-border/60 bg-background">
            <div className={cn('pointer-events-none absolute inset-0 bg-gradient-to-br opacity-60', connector.enabled ? 'from-violet-500/25 to-cyan-500/10' : 'from-zinc-500/20 to-zinc-500/5')} />
            <Cube weight="duotone" className="relative size-5 text-violet-300" />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">{connector.name}</span>
              {connector.hasAuthSecret ? <LockKey weight="duotone" className="size-3.5 shrink-0 text-muted-foreground/70" /> : null}
            </div>
            <span className="truncate text-[11px] text-muted-foreground/80" title={connector.url}>{connector.url}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => onToggle(connector)}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors disabled:opacity-50',
              connector.enabled ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300' : 'border-zinc-400/20 bg-zinc-500/10 text-zinc-300',
            )}
          >
            {connector.enabled ? 'Enabled' : 'Disabled'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onTest(connector.id)}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-violet-500/40 px-3 text-xs font-semibold text-violet-300 transition-colors hover:bg-violet-500/10 disabled:opacity-50"
          >
            <ArrowsClockwise weight="bold" className="size-3.5" />
            Test
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(connector.id, connector.name)}
            aria-label={`Remove ${connector.name}`}
            className="grid size-8 shrink-0 place-items-center rounded-full border border-border/70 text-muted-foreground transition-colors hover:border-red-500/40 hover:text-red-400 disabled:opacity-50"
          >
            <Trash weight="bold" className="size-4" />
          </button>
        </div>
      </div>
      {health ? (
        <div className={cn(
          'flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px]',
          health.ok ? 'border-emerald-400/20 bg-emerald-500/[0.06] text-emerald-300' : 'border-orange-400/25 bg-orange-500/[0.06] text-orange-300',
        )}>
          {health.ok ? <CheckCircle weight="fill" className="size-3.5 shrink-0" /> : <WarningCircle weight="duotone" className="size-3.5 shrink-0" />}
          {health.ok ? (
            <span className="truncate">
              {health.toolCount} tool{health.toolCount === 1 ? '' : 's'}
              {health.toolNames.length > 0 ? `: ${health.toolNames.join(', ')}` : ''}
            </span>
          ) : (
            <span className="truncate">{health.error ?? 'Connection failed.'}</span>
          )}
        </div>
      ) : null}
    </article>
  );
}

function McpConnectorsSection({
  connectors,
  error,
  busyId,
  nameInput,
  urlInput,
  headerNameInput,
  secretInput,
  onNameInput,
  onUrlInput,
  onHeaderNameInput,
  onSecretInput,
  creating,
  onCreate,
  onToggle,
  onTest,
  onDelete,
}: {
  connectors: UserMcpConnectorSummary[];
  error: string | null;
  busyId: string | null;
  nameInput: string;
  urlInput: string;
  headerNameInput: string;
  secretInput: string;
  onNameInput: (v: string) => void;
  onUrlInput: (v: string) => void;
  onHeaderNameInput: (v: string) => void;
  onSecretInput: (v: string) => void;
  creating: boolean;
  onCreate: () => void;
  onToggle: (connector: UserMcpConnectorSummary) => void;
  onTest: (id: string) => void;
  onDelete: (id: string, name: string) => void;
}) {
  return (
    <div id="mcp-connectors" className="flex flex-col gap-3 scroll-mt-24">
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-2">
          <Cube weight="duotone" className="size-4 text-violet-400" />
          <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">Custom connectors (MCP)</h3>
        </div>
        <span className="text-[11px] text-muted-foreground/70">Bring your own Model Context Protocol server</span>
      </div>

      <p className="text-xs leading-5 text-muted-foreground">
        Add your own MCP server by URL. Its tools become available in your chats alongside the app connectors above. The server must be reachable over <span className="font-mono">https</span>; add an auth header if it needs one.
      </p>

      {error ? (
        <div className="rounded-xl border border-orange-500/25 bg-orange-500/[0.06] p-3 text-xs leading-5 text-orange-600 dark:text-orange-200/90">
          {error}
        </div>
      ) : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          onCreate();
        }}
        className="flex flex-col gap-2 rounded-xl border border-border/50 bg-card/40 p-3"
      >
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            name="mcpName"
            required
            value={nameInput}
            onChange={(event) => onNameInput(event.target.value)}
            placeholder="Name — e.g. My Notion MCP"
            className="h-9 w-full rounded-full border border-border/80 bg-background px-4 text-xs font-medium outline-none transition-colors hover:bg-muted/30 focus:border-violet-500/50 sm:w-56"
          />
          <div className="relative flex-1">
            <LinkSimple weight="bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
            <input
              name="mcpUrl"
              required
              value={urlInput}
              onChange={(event) => onUrlInput(event.target.value)}
              placeholder="https://mcp.example.com/mcp"
              className="h-9 w-full rounded-full border border-border/80 bg-background pl-9 pr-4 text-xs font-medium outline-none transition-colors hover:bg-muted/30 focus:border-violet-500/50"
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            name="mcpHeaderName"
            value={headerNameInput}
            onChange={(event) => onHeaderNameInput(event.target.value)}
            placeholder="Auth header (optional) — e.g. Authorization"
            className="h-9 w-full rounded-full border border-border/80 bg-background px-4 text-xs font-medium outline-none transition-colors hover:bg-muted/30 focus:border-violet-500/50 sm:w-56"
          />
          <div className="relative flex-1">
            <LockKey weight="bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
            <input
              name="mcpSecret"
              type="password"
              value={secretInput}
              onChange={(event) => onSecretInput(event.target.value)}
              placeholder="Auth secret (optional, write-only) — e.g. Bearer token"
              autoComplete="new-password"
              className="h-9 w-full rounded-full border border-border/80 bg-background pl-9 pr-4 text-xs font-medium outline-none transition-colors hover:bg-muted/30 focus:border-violet-500/50"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={creating || !nameInput.trim() || !urlInput.trim()}
            className="h-9 shrink-0 rounded-full bg-violet-500 px-4 text-xs font-semibold text-white hover:bg-violet-400"
          >
            <Plus weight="bold" className="mr-1 size-3.5" />
            Add MCP server
          </Button>
        </div>
      </form>

      {connectors.length > 0 ? (
        <div className="flex flex-col gap-2">
          {connectors.map((connector) => (
            <McpConnectorRow
              key={connector.id}
              connector={connector}
              busy={busyId === connector.id}
              onToggle={onToggle}
              onTest={onTest}
              onDelete={onDelete}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border/70 px-4 py-6 text-center text-xs text-muted-foreground">
          No custom MCP servers yet. Add one above, then Test it to see which tools it exposes.
        </div>
      )}
    </div>
  );
}

function BrowserLoginsSection({
  connections,
  enabled,
  error,
  busyId,
  domainInput,
  onDomainInputChange,
  creating,
  onCreate,
  onStartLogin,
  onRefresh,
  onDelete,
}: {
  connections: AuthConnectionSummary[];
  enabled: boolean;
  error: string | null;
  busyId: string | null;
  domainInput: string;
  onDomainInputChange: (value: string) => void;
  creating: boolean;
  onCreate: () => void;
  onStartLogin: (connectionId: string) => void;
  onRefresh: (connectionId: string) => void;
  onDelete: (connectionId: string) => Promise<void>;
}) {
  return (
    <div id="browser-logins" className="flex flex-col gap-3 scroll-mt-24">
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-2">
          <LockKey weight="duotone" className="size-4 text-violet-400" />
          <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">Browser logins</h3>
        </div>
        <span className="text-[11px] text-muted-foreground/70">Sign in once — the agent reuses your session</span>
      </div>

      <p className="text-xs leading-5 text-muted-foreground">
        Authenticate to any website through Kernel’s hosted login. The agent’s cloud browsers start already signed in with that profile — no re-login each task.
      </p>

      {!enabled ? (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4 text-sm leading-6 text-amber-600 dark:text-amber-200/90">
          Browser logins need a Kernel API key in this environment. Add <span className="font-mono">KERNEL_API_KEY</span> to activate this section.
        </div>
      ) : (
        <>
          {error ? (
            <div className="rounded-xl border border-orange-500/25 bg-orange-500/[0.06] p-3 text-xs leading-5 text-orange-600 dark:text-orange-200/90">
              {error}
            </div>
          ) : null}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              onCreate();
            }}
            className="flex flex-col gap-2 rounded-xl border border-border/50 bg-card/40 p-3 sm:flex-row sm:items-center"
          >
            <div className="relative flex-1">
              <GlobeSimple weight="bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
              <input
                name="domain"
                required
                value={domainInput}
                onChange={(event) => onDomainInputChange(event.target.value)}
                placeholder="Connect a site — e.g. amazon.com"
                className="h-9 w-full rounded-full border border-border/80 bg-background pl-9 pr-4 text-xs font-medium outline-none transition-colors hover:bg-muted/30 focus:border-violet-500/50"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              disabled={creating || !domainInput.trim()}
              className="h-9 shrink-0 rounded-full bg-violet-500 px-4 text-xs font-semibold text-white hover:bg-violet-400"
            >
              <Plus weight="bold" className="mr-1 size-3.5" />
              Connect a site
            </Button>
          </form>

          {connections.length > 0 ? (
            <div className="flex flex-col gap-2">
              {connections.map((connection) => (
                <BrowserLoginRow
                  key={connection.id}
                  connection={connection}
                  busy={busyId === connection.id}
                  onStartLogin={onStartLogin}
                  onRefresh={onRefresh}
                  onDelete={onDelete}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border/70 px-4 py-6 text-center text-xs text-muted-foreground">
              No browser logins yet. Add a site above, open its hosted login page, and finish signing in — the agent will reuse it.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ConnectionsClient() {
  useDocumentTitle('Connections');
  const router = useRouter();

  const [composioEnabled, setComposioEnabled] = useState(false);
  const [accounts, setAccounts] = useState<ComposioConnectedAccountSummary[]>([]);
  const [toolkits, setToolkits] = useState<ComposioToolkitSummary[]>([]);
  const [sessionActive, setSessionActive] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [authorizeError, setAuthorizeError] = useState<string | null>(null);
  const [authorizingSlug, setAuthorizingSlug] = useState<string | null>(null);

  const [browserLoginsOn, setBrowserLoginsOn] = useState(false);
  const [browserLoginConnections, setBrowserLoginConnections] = useState<AuthConnectionSummary[]>([]);
  const [browserLoginError, setBrowserLoginError] = useState<string | null>(null);
  const [browserLoginBusyId, setBrowserLoginBusyId] = useState<string | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [creatingBrowserLogin, setCreatingBrowserLogin] = useState(false);

  const [mcpConnectors, setMcpConnectors] = useState<UserMcpConnectorSummary[]>([]);
  const [mcpError, setMcpError] = useState<string | null>(null);
  const [mcpBusyId, setMcpBusyId] = useState<string | null>(null);
  const [mcpName, setMcpName] = useState('');
  const [mcpUrl, setMcpUrl] = useState('');
  const [mcpHeaderName, setMcpHeaderName] = useState('');
  const [mcpSecret, setMcpSecret] = useState('');
  const [creatingMcp, setCreatingMcp] = useState(false);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await fetch('/api/connections');
      if (!res.ok) throw new Error(`Failed to load connections (${res.status})`);
      const data = await res.json();
      setComposioEnabled(Boolean(data.composioEnabled));
      setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
      setToolkits(Array.isArray(data.toolkits) ? data.toolkits : []);
      setSessionActive(Boolean(data.sessionActive));
      setLoadError(data.loadError ?? null);
      setCatalogError(data.catalogError ?? null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load connected app data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBrowserLogins = useCallback(async () => {
    try {
      const res = await fetch('/api/connections/browser-logins');
      if (!res.ok) throw new Error(`Failed to load browser logins (${res.status})`);
      const data = await res.json();
      setBrowserLoginsOn(Boolean(data.enabled));
      setBrowserLoginConnections(Array.isArray(data.connections) ? data.connections : []);
      setBrowserLoginError(data.error ?? null);
    } catch (err) {
      setBrowserLoginError(err instanceof Error ? err.message : 'Could not load browser logins.');
    }
  }, []);

  const fetchMcpConnectors = useCallback(async () => {
    try {
      const res = await fetch('/api/connections/mcp');
      if (!res.ok) throw new Error(`Failed to load MCP connectors (${res.status})`);
      const data = await res.json();
      setMcpConnectors(Array.isArray(data.connectors) ? data.connectors : []);
    } catch (err) {
      setMcpError(err instanceof Error ? err.message : 'Could not load MCP connectors.');
    }
  }, []);

  useEffect(() => {
    void fetchConnections();
    void fetchBrowserLogins();
    void fetchMcpConnectors();
  }, [fetchConnections, fetchBrowserLogins, fetchMcpConnectors]);

  const handleCreateMcp = useCallback(async () => {
    const name = mcpName.trim();
    const url = mcpUrl.trim();
    if (!name || !url) return;
    setCreatingMcp(true);
    setMcpError(null);
    try {
      const res = await fetch('/api/connections/mcp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          url,
          authHeaderName: mcpHeaderName.trim() || undefined,
          authSecret: mcpSecret.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not add that server (${res.status})`);
      }
      setMcpName('');
      setMcpUrl('');
      setMcpHeaderName('');
      setMcpSecret('');
      await fetchMcpConnectors();
      toast.success(`Added ${name}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not add that MCP server.';
      setMcpError(message);
      toast.error(message);
    } finally {
      setCreatingMcp(false);
    }
  }, [mcpName, mcpUrl, mcpHeaderName, mcpSecret, fetchMcpConnectors]);

  const handleToggleMcp = useCallback(async (connector: UserMcpConnectorSummary) => {
    setMcpBusyId(connector.id);
    setMcpError(null);
    try {
      const res = await fetch(`/api/connections/mcp/${encodeURIComponent(connector.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !connector.enabled }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not update (${res.status})`);
      }
      if (data?.connector) {
        setMcpConnectors((prev) => prev.map((item) => (item.id === data.connector.id ? data.connector : item)));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not update connector.';
      setMcpError(message);
      toast.error(message);
    } finally {
      setMcpBusyId(null);
    }
  }, []);

  const handleTestMcp = useCallback(async (id: string) => {
    setMcpBusyId(id);
    setMcpError(null);
    try {
      const res = await fetch(`/api/connections/mcp/${encodeURIComponent(id)}/test`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not test (${res.status})`);
      }
      if (data?.health) {
        setMcpConnectors((prev) => prev.map((item) => (item.id === id ? { ...item, lastHealth: data.health } : item)));
        if (data.health.ok) toast.success(`Found ${data.health.toolCount} tool${data.health.toolCount === 1 ? '' : 's'}`);
        else toast.error(data.health.error ?? 'Connection failed.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not test connector.';
      setMcpError(message);
      toast.error(message);
    } finally {
      setMcpBusyId(null);
    }
  }, []);

  const handleDeleteMcp = useCallback(async (id: string, name: string) => {
    setMcpBusyId(id);
    setMcpError(null);
    try {
      const res = await fetch(`/api/connections/mcp/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not remove (${res.status})`);
      }
      setMcpConnectors((prev) => prev.filter((item) => item.id !== id));
      toast.success(`Removed ${name}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not remove connector.';
      setMcpError(message);
      toast.error(message);
    } finally {
      setMcpBusyId(null);
    }
  }, []);

  const handleAuthorize = useCallback(async (connector: Connector) => {
    setAuthorizeError(null);
    setAuthorizingSlug(connector.slug);
    try {
      const res = await fetch('/api/connections/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toolkit: connector.slug }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Connect failed (${res.status})`);
      }
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl;
        return;
      }
      router.push(chatHref(connector.prompt));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start that connection.';
      setAuthorizeError(message);
      toast.error(message);
    } finally {
      setAuthorizingSlug(null);
    }
  }, [router]);

  const handleCreateBrowserLogin = useCallback(async () => {
    const domain = domainInput.trim();
    if (!domain) return;
    setCreatingBrowserLogin(true);
    setBrowserLoginError(null);
    try {
      const res = await fetch('/api/connections/browser-logins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not connect that site (${res.status})`);
      }
      setDomainInput('');
      await fetchBrowserLogins();
      toast.success(`Started login for ${domain}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not connect that site.';
      setBrowserLoginError(message);
      toast.error(message);
    } finally {
      setCreatingBrowserLogin(false);
    }
  }, [domainInput, fetchBrowserLogins]);

  const handleStartLogin = useCallback(async (connectionId: string) => {
    setBrowserLoginBusyId(connectionId);
    setBrowserLoginError(null);
    try {
      const res = await fetch(`/api/connections/browser-logins/${encodeURIComponent(connectionId)}/login`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not start login (${res.status})`);
      }
      await fetchBrowserLogins();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start login.';
      setBrowserLoginError(message);
      toast.error(message);
    } finally {
      setBrowserLoginBusyId(null);
    }
  }, [fetchBrowserLogins]);

  const handleRefreshConnection = useCallback(async (connectionId: string) => {
    setBrowserLoginBusyId(connectionId);
    try {
      const res = await fetch(`/api/connections/browser-logins/${encodeURIComponent(connectionId)}`);
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not refresh status (${res.status})`);
      }
      if (data?.connection) {
        setBrowserLoginConnections((prev) => prev.map((item) => (item.id === data.connection.id ? data.connection : item)));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not refresh status.';
      setBrowserLoginError(message);
      toast.error(message);
    } finally {
      setBrowserLoginBusyId(null);
    }
  }, []);

  const handleDeleteConnection = useCallback(async (connectionId: string) => {
    setBrowserLoginBusyId(connectionId);
    try {
      const res = await fetch(`/api/connections/browser-logins/${encodeURIComponent(connectionId)}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Could not remove login (${res.status})`);
      }
      setBrowserLoginConnections((prev) => prev.filter((item) => item.id !== connectionId));
      toast.success('Browser login removed');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not remove login.';
      setBrowserLoginError(message);
      toast.error(message);
    } finally {
      setBrowserLoginBusyId(null);
    }
  }, []);

  const accountsBySlug = useMemo(() => findLatestByToolkit(accounts), [accounts]);
  const availableConnectors = useMemo(() => mergeConnectors(toolkits, !query.trim()), [toolkits, query]);
  const featuredConnectors = useMemo(
    () => FEATURED_CONNECTORS.map((connector) => availableConnectors.find((available) => available.slug === connector.slug) ?? connector),
    [availableConnectors],
  );
  const activeCount = useMemo(() => accounts.filter((account) => normalizeStatus(account) === 'active').length, [accounts]);
  const attentionCount = useMemo(
    () => accounts.filter((account) => { const status = normalizeStatus(account); return status === 'expired' || status === 'disabled'; }).length,
    [accounts],
  );

  const trimmedQuery = query.trim().toLowerCase();
  const allCategories = useMemo(() => [
    'All',
    ...CATEGORY_ORDER,
    ...availableConnectors.map((connector) => connector.category).filter((c) => !CATEGORY_ORDER.includes(c as (typeof CATEGORY_ORDER)[number])),
  ].filter((c, i, arr) => arr.indexOf(c) === i), [availableConnectors]);

  const filteredConnectors = useMemo(() => availableConnectors.filter((connector) => {
    const matchesQuery = !trimmedQuery
      || connector.name.toLowerCase().includes(trimmedQuery)
      || connector.description.toLowerCase().includes(trimmedQuery)
      || connector.slug.toLowerCase().includes(trimmedQuery);
    const matchesCategory = category === 'All' || connector.category === category;
    return matchesQuery && matchesCategory;
  }), [availableConnectors, trimmedQuery, category]);

  const orderedCategories = useMemo(() => [
    ...CATEGORY_ORDER,
    ...filteredConnectors.map((connector) => connector.category).filter((c) => !CATEGORY_ORDER.includes(c as (typeof CATEGORY_ORDER)[number])),
  ].filter((c, i, arr) => arr.indexOf(c) === i), [filteredConnectors]);

  const groupedConnectors = useMemo(
    () => orderedCategories
      .map((cat) => ({ category: cat, connectors: filteredConnectors.filter((connector) => connector.category === cat) }))
      .filter((group) => group.connectors.length > 0),
    [orderedCategories, filteredConnectors],
  );

  const knownConnectorSlugs = useMemo(() => new Set(availableConnectors.map((connector) => connector.slug)), [availableConnectors]);
  const otherConnectedAccounts = useMemo(() => accounts
    .filter((account) => !knownConnectorSlugs.has(account.toolkitSlug))
    .sort((a, b) => {
      const aTime = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
      const bTime = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
      return bTime - aTime;
    }), [accounts, knownConnectorSlugs]);

  return (
    <main className="min-h-screen bg-background p-2 md:p-3">
      <Toaster position="top-center" richColors closeButton />
      <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <div className="flex items-center gap-2">
            <PlugsConnected weight="duotone" className="size-4 text-violet-400" />
            <h1 className="text-sm font-semibold tracking-wide">Connectors</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden rounded-full border border-border/70 px-3 py-1 text-xs text-muted-foreground sm:inline-flex">
              {activeCount} active{attentionCount > 0 ? ` · ${attentionCount} need attention` : ''}
            </span>
            <Button asChild size="sm" className="h-8 rounded-full bg-violet-500 px-3 text-xs font-semibold text-white hover:bg-violet-400">
              <Link href={chatHref('Help me choose which apps to connect for my workflow.')}>
                <Plus className="mr-1 size-3.5" weight="bold" />
                Ask Galexia
              </Link>
            </Button>
          </div>
        </header>

        <section className="relative overflow-hidden border-b border-border/40 px-4 py-10 md:px-8">
          <div className="relative z-10 max-w-2xl">
            <h2 className="bg-gradient-to-b from-foreground to-muted-foreground/60 bg-clip-text text-xl font-extrabold uppercase tracking-tight text-transparent md:text-2xl">
              Connect your apps to Galexia
            </h2>
            <div className="mt-3 flex flex-col gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2.5">
                <LinkSimple weight="bold" className="size-4 shrink-0 text-violet-400" />
                <p><span className="font-medium text-foreground">A connector</span> links Galexia directly to your accounts — search, draft, update, and automate across them.</p>
              </div>
              <div className="flex items-center gap-2.5">
                <PuzzlePiece weight="duotone" className="size-4 shrink-0 text-violet-400" />
                <p><span className="font-medium text-foreground">Combine connectors</span> to give the agent your full stack in one conversation.</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground/70">
              Connection session: {sessionActive ? 'on' : composioEnabled ? 'ready' : 'off'}
            </p>
          </div>
          <div className="pointer-events-none absolute -top-10 right-8 h-44 w-72 rounded-full bg-violet-500/10 blur-[80px]" />
        </section>

        <section className="mx-auto flex w-full max-w-5xl flex-col gap-8 p-4 md:p-8">

          {!composioEnabled && !loading ? (
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4 text-sm leading-6 text-amber-600 dark:text-amber-200/90">
              Connected app tools are not enabled in this environment. Add the platform API key and enable app tools to activate this page.
            </div>
          ) : null}

          {loadError ? (
            <div className="rounded-xl border border-orange-500/25 bg-orange-500/[0.06] p-4 text-sm leading-6 text-orange-600 dark:text-orange-200/90">
              Connection status could not be refreshed. You can still start a connection from chat.
            </div>
          ) : null}

          {catalogError ? (
            <div className="rounded-xl border border-orange-500/25 bg-orange-500/[0.06] p-4 text-sm leading-6 text-orange-600 dark:text-orange-200/90">
              The app catalog could not be refreshed, so this page is showing the core supported apps.
            </div>
          ) : null}

          {authorizeError ? (
            <div className="rounded-xl border border-orange-500/25 bg-orange-500/[0.06] p-4 text-sm leading-6 text-orange-600 dark:text-orange-200/90">
              Could not start that connection. Try again, or ask Galexia to create the connection link in chat.
            </div>
          ) : null}

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-xs">
              <MagnifyingGlass weight="bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
              <input
                name="q"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search app connectors…"
                className="h-8 w-full rounded-full border border-border/80 bg-background pl-9 pr-4 text-xs font-medium outline-none transition-colors hover:bg-muted/30 focus:border-violet-500/50"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {allCategories.map((cat) => {
                const active = category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs transition-colors',
                      active ? 'border-violet-500/50 bg-violet-500/10 text-foreground' : 'border-border/70 text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">Featured</h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {featuredConnectors.map((connector) => (
                <ConnectorCard
                  key={connector.slug}
                  connector={connector}
                  account={accountsBySlug.get(connector.slug)}
                  busy={authorizingSlug === connector.slug}
                  onAuthorize={handleAuthorize}
                />
              ))}
            </div>
          </div>

          <BrowserLoginsSection
            connections={browserLoginConnections}
            enabled={browserLoginsOn}
            error={browserLoginError}
            busyId={browserLoginBusyId}
            domainInput={domainInput}
            onDomainInputChange={setDomainInput}
            creating={creatingBrowserLogin}
            onCreate={() => void handleCreateBrowserLogin()}
            onStartLogin={(id) => void handleStartLogin(id)}
            onRefresh={(id) => void handleRefreshConnection(id)}
            onDelete={handleDeleteConnection}
          />

          <McpConnectorsSection
            connectors={mcpConnectors}
            error={mcpError}
            busyId={mcpBusyId}
            nameInput={mcpName}
            urlInput={mcpUrl}
            headerNameInput={mcpHeaderName}
            secretInput={mcpSecret}
            onNameInput={setMcpName}
            onUrlInput={setMcpUrl}
            onHeaderNameInput={setMcpHeaderName}
            onSecretInput={setMcpSecret}
            creating={creatingMcp}
            onCreate={() => void handleCreateMcp()}
            onToggle={(connector) => void handleToggleMcp(connector)}
            onTest={(id) => void handleTestMcp(id)}
            onDelete={(id, name) => void handleDeleteMcp(id, name)}
          />

          {groupedConnectors.length > 0 ? groupedConnectors.map((group) => (
            <div key={group.category} className="flex flex-col gap-3">
              <div className="flex items-end justify-between">
                <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">{group.category}</h3>
                <Link
                  href={chatHref(`Show me useful ${group.category.toLowerCase()} connections I can set up.`)}
                  className="hidden items-center gap-1 text-xs font-medium text-violet-400 transition-colors hover:text-violet-300 sm:inline-flex"
                >
                  Ask Galexia
                  <LinkSimple weight="bold" className="size-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {group.connectors.map((connector) => (
                  <ConnectorCard
                    key={connector.slug}
                    connector={connector}
                    account={accountsBySlug.get(connector.slug)}
                    busy={authorizingSlug === connector.slug}
                    onAuthorize={handleAuthorize}
                  />
                ))}
              </div>
            </div>
          )) : !loading ? (
            <div className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-border px-6 text-center">
              <h2 className="text-base font-semibold">No connectors found</h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                Try a different search or ask Galexia to help you choose the right app connection.
              </p>
              <Button asChild size="sm" className="mt-5 rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400">
                <Link href={chatHref('Help me choose which apps to connect for my workflow.')}>Ask Galexia</Link>
              </Button>
            </div>
          ) : null}

          {otherConnectedAccounts.length > 0 ? (
            <div className="flex flex-col gap-3">
              <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">Other connected accounts</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {otherConnectedAccounts.map((account) => {
                  const status = normalizeStatus(account);
                  return (
                    <article key={account.id} className="flex h-[72px] items-center gap-3 rounded-xl border border-border/50 bg-card/60 p-3">
                      <div className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-border/60 bg-background">
                        <PuzzlePiece weight="duotone" className="size-5 text-violet-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="truncate text-sm font-medium capitalize">{account.toolkitSlug.replaceAll('_', ' ')}</h4>
                          <Badge className={cn('shrink-0 gap-1 rounded-full border px-2 py-0.5 text-[10px]', statusClasses(status))}>
                            <StatusIcon status={status} />
                            {statusLabel(status)}
                          </Badge>
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {account.statusReason ?? 'Available to Galexia when this account is active.'}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
